import { Button, FormDrawer, FormDrawerContent, Id, Loading, TextareaField, Trigger, isSuccess } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { isBefore } from "date-fns";
import { FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { createEventUserService } from "../../../../shared/api/userService";
import { CostCenterType } from "../../../../shared/types/organizationalUnitUser";
import { eventService } from "../../shared/api/eventService";
import {
	CancelEventUserParticipationFormValues,
	CreateEventUserRequestRequest,
	EventDetailResource,
} from "../../shared/types";
import { CreateEventUserRequestFormValues, EventUserRequestCreateDrawer } from "./EventUserRequestCreateDrawer";

const useEventParticipationAction = (eventId: Id) => {
	const queryClient = useQueryClient();
	const [isLoading, setIsLoading] = useState(false);
	const eventUserService = useMemo(() => createEventUserService(eventId), [eventId]);

	const createUserRequest = async (formValues: CreateEventUserRequestFormValues) => {
		setIsLoading(true);
		try {
			const request: CreateEventUserRequestRequest = {
				costs: formValues.costs,
				currencyIsoCode: formValues.currency?.isoCode as string,
				requestedApproverId: formValues.requestedApprover?.userId as Id,
				costCenterId: formValues.requestedApprover?.costCenterId as Id,
				costCenterType: formValues.requestedApprover?.costCenterType as CostCenterType,
				participationReason: formValues.participationReason,
			};
			const response = await eventService.createUserRequest(eventId, request);
			if (isSuccess(response)) {
				await queryClient.invalidateQueries({ queryKey: eventService.detailsKey(eventId) });
				return true;
			}
			return false;
		} finally {
			setIsLoading(false);
		}
	};

	const participate = async () => {
		setIsLoading(true);
		try {
			const response = await eventService.participate(eventId);
			if (isSuccess(response)) {
				await queryClient.invalidateQueries({ queryKey: eventService.detailsKey(eventId) });
				await queryClient.invalidateQueries({ queryKey: eventUserService.indexKey });
			}
		} finally {
			setIsLoading(false);
		}
	};

	const cancelParticipationWithRequest = async (values: CancelEventUserParticipationFormValues) => {
		setIsLoading(true);
		try {
			const response = await eventService.cancelParticipationWithInquiry(eventId, values);
			if (isSuccess(response)) {
				await queryClient.invalidateQueries({ queryKey: eventService.detailsKey(eventId) });
				await queryClient.invalidateQueries({ queryKey: eventUserService.indexKey });
			}
		} finally {
			setIsLoading(false);
		}
	};

	const cancelParticipation = async () => {
		setIsLoading(true);
		try {
			const response = await eventService.cancelParticipation(eventId);
			if (isSuccess(response)) {
				await queryClient.invalidateQueries({ queryKey: eventService.detailsKey(eventId) });
				await queryClient.invalidateQueries({ queryKey: eventUserService.indexKey });
			}
		} finally {
			setIsLoading(false);
		}
	};

	return {
		isLoading,
		cancelParticipation,
		cancelParticipationWithRequest,
		createUserRequest,
		participate,
	};
};

interface CancelEventParticipationActionProps {
	eventId: Id;
	requiresParticipationApproval: boolean;
	title: string;
}

const CancelEventParticipationAction: FC<CancelEventParticipationActionProps> = ({
	eventId,
	requiresParticipationApproval,
	title,
}) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState<boolean>(false);
	const { isLoading, cancelParticipation, cancelParticipationWithRequest } = useEventParticipationAction(eventId);

	const validate = useCallback(
		(values: CancelEventUserParticipationFormValues) => {
			const errors: { [K in keyof CancelEventUserParticipationFormValues]?: any } = {};

			if (!values.cancellationReason) {
				errors.cancellationReason = t("Required field");
			}
			return errors;
		},
		[t],
	);

	return (
		<>
			<Button
				color="danger"
				type="button"
				onClick={() => (requiresParticipationApproval ? setOpen(true) : cancelParticipation())}
				disabled={isLoading}
				prefix={isLoading && <Loading variant="tiny" color="current" />}
			>
				{title}
			</Button>
			<FormDrawer
				renderForm={(props) => (
					<FormDrawerContent {...props}>
						<TextareaField name="cancellationReason" label={t("Cancellation reason")} required />
					</FormDrawerContent>
				)}
				open={open}
				onClose={() => setOpen(false)}
				initialValues={{ cancellationReason: "" }}
				onSubmit={async (values) => await cancelParticipationWithRequest(values)}
				title={title}
				validate={validate}
			/>
		</>
	);
};

interface CancelEventRequestActionProps {
	eventId: Id;
}

const CancelEventRequestAction: FC<CancelEventRequestActionProps> = ({ eventId }) => {
	const { t } = useTranslation();
	const { isLoading, cancelParticipation } = useEventParticipationAction(eventId);

	return (
		<Button
			color="danger"
			type="button"
			onClick={cancelParticipation}
			disabled={isLoading}
			prefix={isLoading && <Loading variant="tiny" color="current" />}
		>
			{t("Cancel request")}
		</Button>
	);
};

interface ParticipateEventActionProps {
	eventId: Id;
	requiresParticipationApproval: boolean;
}

const ParticipateEventAction: FC<ParticipateEventActionProps> = ({ eventId, requiresParticipationApproval }) => {
	const { t } = useTranslation();
	const { isLoading, createUserRequest, participate } = useEventParticipationAction(eventId);

	if (requiresParticipationApproval) {
		return (
			<Trigger
				render={(props) => (
					<EventUserRequestCreateDrawer onClose={props.handleClose} open={props.open} onSubmit={createUserRequest} />
				)}
			>
				{(triggerProps) => (
					<Button
						{...triggerProps}
						type="button"
						color="primary"
						disabled={isLoading}
						prefix={isLoading && <Loading variant="tiny" color="current" />}
					>
						{t("Participate in the event")}
					</Button>
				)}
			</Trigger>
		);
	} else {
		return (
			<Button
				type="button"
				color="primary"
				onClick={participate}
				disabled={isLoading}
				prefix={isLoading && <Loading variant="tiny" color="current" />}
			>
				{t("Participate in the event")}
			</Button>
		);
	}
};

interface Props {
	event: EventDetailResource;
}
export const EventParticipationAction: FC<Props> = ({ event }) => {
	const { t } = useTranslation();
	const hasEventEnded = isBefore(event.end, new Date());

	if (hasEventEnded) {
		if (event.userParticipationStatus !== "participating") {
			return null;
		}
		return (
			<CancelEventParticipationAction
				eventId={event.id}
				requiresParticipationApproval={event.requiresParticipationApproval}
				title={t("I did not participate")}
			/>
		);
	}

	switch (event.userParticipationStatus) {
		case "participating":
			return (
				<CancelEventParticipationAction
					eventId={event.id}
					requiresParticipationApproval={event.requiresParticipationApproval}
					title={t("Cancel participation")}
				/>
			);
		case "request":
			return <CancelEventRequestAction eventId={event.id} />;
		case "none":
			if (!event.canParticipate) {
				return null;
			}
			return (
				<ParticipateEventAction
					eventId={event.id}
					requiresParticipationApproval={event.requiresParticipationApproval}
				/>
			);
	}
};
